import { useRef } from 'react';
import { useInView } from 'framer-motion';

const LETTERS = [
  ...'VISUAL'.split('').map((ch) => ({ ch, accent: false })),
  ...'RY'.split('').map((ch) => ({ ch, accent: true })),
];

export const BrandSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const subtitleInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section className="relative overflow-hidden border-t border-border-subtle bg-gradient-to-b from-background via-[#0d0a06] to-[#0a0805]">
      {/* Ambient glow behind text */}
      <div
        className="pointer-events-none absolute inset-0 select-none"
        aria-hidden="true"
      >
        <div
          className="absolute left-1/2 top-1/2 h-[60vh] w-[80vw] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-80"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(244,196,95,0.04) 0%, rgba(244,196,95,0.01) 50%, transparent 70%)',
          }}
        />
        <div
          className="absolute left-1/2 top-1/2 h-[40vh] w-[50vw] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(244,196,95,0.02) 0%, transparent 70%)',
          }}
        />
      </div>

      {/* Noise texture overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03] mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJmIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iLjc0IiBudW1PY3RhdmVzPSIzIiAvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNmKSIgLz48L3N2Zz4=")',
          backgroundSize: '256px 256px',
        }}
      />

      <div className="mx-auto w-full min-w-0 max-w-7xl px-3 sm:px-6">
        <div ref={ref} className="relative py-24 sm:py-32 lg:py-40">
          {/* Decorative rule above */}
          <div className="absolute left-3 right-3 top-0 sm:left-6 sm:right-6">
            <div className="h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent" />
            <div className="mx-auto mt-px h-px w-1/3 bg-gradient-to-r from-transparent via-accent/10 to-transparent" />
          </div>

          {/* Decorative rule below */}
          <div className="absolute bottom-0 left-3 right-3 sm:left-6 sm:right-6">
            <div className="h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent" />
            <div className="mx-auto mt-px h-px w-1/3 bg-gradient-to-r from-transparent via-accent/10 to-transparent" />
          </div>

          {/* Letters — CSS-driven reveal to avoid framer-motion
              IntersectionObserver crashes during rapid scroll.
              perspective on the parent gives rotateX depth. */}
          <div
            className="flex flex-wrap items-baseline justify-center gap-0"
            style={{ perspective: '800px' }}
          >
            {LETTERS.map(({ ch, accent }, i) => (
              <span
                key={ch}
                className={`
                  font-serif font-bold leading-[0.85] tracking-[-0.03em]
                  text-[clamp(4rem,18vw,14rem)]
                  select-none
                  transition-[opacity,transform] duration-[600ms] ease-[cubic-bezier(0.25,0.1,0.15,1)]
                  ${isInView ? 'opacity-100' : 'opacity-0'}
                  ${accent ? 'text-accent' : 'text-foreground'}
                `}
                style={{
                  transitionDelay: `${100 + i * 40}ms`,
                  transform: isInView
                    ? 'translateY(0) rotateX(0deg)'
                    : 'translateY(40px) rotateX(-15deg)',
                  textShadow: accent
                    ? '0 0 40px rgba(244,196,95,0.15), 0 0 80px rgba(244,196,95,0.08)'
                    : '0 0 40px rgba(251,247,236,0.04)',
                }}
              >
                {ch}
              </span>
            ))}
          </div>

          {/* Subtitle line */}
          <p
            className={`mt-6 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted/60 sm:text-xs transition-[opacity,transform] duration-[800ms] ease-[cubic-bezier(0.25,0.1,0.15,1)] ${
              subtitleInView ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              transitionDelay: '600ms',
              transform: subtitleInView ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            AI-powered visual storytelling
          </p>
        </div>
      </div>
    </section>
  );
};
